import { useState, useEffect } from 'react';
import { 
  Alert, 
  Button, 
  Box, 
  Typography, 
  Card, 
  CardContent,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  Email, 
  Warning,
  Refresh
} from '@mui/icons-material';
import { getAuth, sendEmailVerification, reload } from 'firebase/auth';

const EmailVerification = ({ user, onVerificationComplete }) => {
  const [verificationSent, setVerificationSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingVerification, setCheckingVerification] = useState(false);

  const auth = getAuth();

  useEffect(() => {
    // Check verification status when component mounts
    if (user && !user.emailVerified) {
      console.log('👤 User email verification status:', user.emailVerified);
    }
  }, [user]);

  const sendVerificationEmail = async () => {
    if (!user) {
      setError('No user is currently signed in.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendEmailVerification(user);
      setVerificationSent(true);
      console.log("✅ Verification email sent!");
      
      // Auto-check verification status every 5 seconds
      setTimeout(() => {
        checkVerificationStatus();
      }, 5000);

    } catch (error) {
      console.error("❌ Error sending verification email:", error);
      setError(`Failed to send verification email: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!user) return;

    setCheckingVerification(true);
    try {
      // Reload user to get latest verification status
      await reload(user);
      
      // Get fresh user object
      const currentUser = auth.currentUser;
      
      if (currentUser && currentUser.emailVerified) {
        console.log("🎉 Email verified successfully!");
        if (onVerificationComplete) {
          onVerificationComplete(currentUser);
        }
      } else {
        console.log("⏳ Email not yet verified");
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setError(`Error checking verification: ${error.message}`);
    } finally {
      setCheckingVerification(false);
    }
  };

  // If user is already verified, don't show this component
  if (user && user.emailVerified) {
    return null;
  }

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', mt: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Warning color="warning" sx={{ mr: 1 }} />
          <Typography variant="h6">
            Email Verification Required
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Please verify your email address to access all features of the dashboard.
          </Typography>
        </Alert>

        {user && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Chip 
              label={user.emailVerified ? "Verified" : "Not Verified"} 
              color={user.emailVerified ? "success" : "warning"}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {verificationSent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              📧 Verification email sent to <strong>{user?.email}</strong>!
              <br />
              Please check your inbox and click the verification link.
            </Typography>
          </Alert>
        )}

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <Email />}
            onClick={sendVerificationEmail}
            disabled={loading || !user}
          >
            {verificationSent ? 'Resend Verification Email' : 'Send Verification Email'}
          </Button>

          <Button
            variant="outlined"
            startIcon={checkingVerification ? <CircularProgress size={16} /> : <Refresh />}
            onClick={checkVerificationStatus}
            disabled={checkingVerification || !user}
          >
            Check Verification Status
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          💡 Tip: After clicking the verification link in your email, click "Check Verification Status" to continue.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default EmailVerification;